import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { ApiError, api, setUnauthorizedHandler, type Me } from '../lib/api';
import { VerifyEmail } from './VerifyEmail';
import { LinkPage } from './LinkPage';
import { setMapAttribution } from './map/mapbase';

function linkTokenFromUrl(): string | null {
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path !== '/welcome' && path !== '/reset-password') return null;
  return new URLSearchParams(window.location.search).get('token');
}

/**
 * Sign-in, and everything behind it.
 *
 * The console used to carry a shared API key in its bundle, so there was
 * nothing to sign into. Now it holds a session like any other application: this
 * asks who is signed in before rendering the board, and puts the form back up
 * the moment a request comes back unauthenticated -- from anywhere, not only
 * from here, because a session can lapse mid-shift with a drawer open.
 */

interface AuthValue {
  me: Me;
  signOut: () => Promise<void>;
  /**
   * Re-read the session.
   *
   * The account carries the formats every screen renders distances and prices
   * with, so changing one has to reach the whole app rather than the card that
   * changed it -- otherwise the fleet page shows miles while the board shows
   * kilometres until the next reload.
   */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth outside AuthGate');
  return value;
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [checking, setChecking] = useState(true);
  /* The message Google's callback redirects back with, if it failed. */
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('authError');
    if (authError) {
      setNotice(authError);
      /*
       * Strip it from the address bar. Left there it would reappear on every
       * reload, long after it stopped being true.
       */
      params.delete('authError');
      const query = params.toString();
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (query ? `?${query}` : ''),
      );
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((value) => {
        if (!cancelled) setMe(value);
      })
      .catch(() => {
        /* Not signed in is the ordinary case here, not an error worth showing. */
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setMe(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => {});
    setMe(null);
  }, []);

  const refresh = useCallback(async () => {
    const next = await api.me().catch(() => null);
    if (next) setMe(next);
  }, []);

  /* Before any map mounts: the credit for whichever tiles the admin panel names. */
  if (me) setMapAttribution(me.maps?.attribution);

  const value = useMemo(
    () => (me ? { me, signOut, refresh } : null),
    [me, signOut, refresh],
  );

  /*
   * An invitation or reset link. Shown before anything else, signed in or not:
   * somebody following a reset link for one address may still have a session
   * for another open in this browser.
   */
  const linkToken = linkTokenFromUrl();
  if (linkToken) return <LinkPage token={linkToken} onSignedIn={setMe} />;

  if (checking) {
    return (
      <div className="auth-shell">
        <div className="auth-loading">Checking your session…</div>
      </div>
    );
  }

  if (!value) {
    return <SignIn notice={notice} onSignedIn={setMe} />;
  }

  /* Signed in, but the email is not confirmed yet: one screen until it is. */
  if (value.me.user && !value.me.user.emailVerified) {
    return <VerifyEmail me={value.me} onVerified={setMe} onSignOut={() => void signOut()} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ------------------------------------------------------------------ */

function SignIn({
  notice,
  onSignedIn,
}: {
  notice: string | null;
  onSignedIn: (me: Me) => void;
}) {
  const [providers, setProviders] = useState<{
    google: boolean;
    signupOpen: boolean;
  } | null>(null);
  /* The marketing site links here with ?signup=1&plan=..., straight into the form. */
  const params = new URLSearchParams(window.location.search);
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>(
    params.get('signup') ? 'signup' : params.get('forgot') ? 'forgot' : 'signin',
  );
  const [sentTo, setSentTo] = useState<string | null>(null);
  const planCode = params.get('plan') ?? undefined;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(notice);
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .providers()
      .then((p) => setProviders({ google: p.google, signupOpen: p.signupOpen }))
      .catch(() => setProviders({ google: false, signupOpen: false }));
  }, []);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'forgot') {
        await api.forgotPassword(email.trim());
        setSentTo(email.trim());
        setBusy(false);
        return;
      }
      if (mode === 'signup') {
        await api.signup({ name, email, password, planCode });
      } else {
        await api.login(email, password);
      }
      onSignedIn(await api.me());
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not reach the server. Is the API running?',
      );
      setBusy(false);
    }
  };

  const google = () => {
    const returnTo = window.location.pathname + window.location.search;
    window.location.href = `/v1/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const signingUp = mode === 'signup';

  if (mode === 'forgot') {
    return (
      <div className="auth-shell">
        <form className="auth-card" onSubmit={submit}>
          <div className="auth-brand">
            <img className="auth-wordmark" src="/brand/allrouted-wordmark.png" alt="AllRouted" width="466" height="72" />
          </div>
          <h1 className="auth-title">Reset your password</h1>
          {sentTo ? (
            <p className="verify-ok" role="status">
              If {sentTo} has an AllRouted account, a link to choose a new password is on its way. It works for two hours.
            </p>
          ) : (
            <>
              <p className="verify-text">Enter the email you sign in with and we will send you a link to choose a new password.</p>
              {error ? (
                <p className="auth-error" role="alert">
                  {error}
                </p>
              ) : null}
              <label className="auth-field">
                <span>Email</span>
                <input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </label>
              <button className="auth-submit" type="submit" disabled={busy}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </>
          )}
          <p className="auth-alt">
            <button
              type="button"
              onClick={() => {
                setMode('signin');
                setSentTo(null);
                setError(null);
              }}
            >
              ← Back to sign in
            </button>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-brand">
          <img className="auth-wordmark" src="/brand/allrouted-wordmark.png" alt="AllRouted" width="466" height="72" />
        </div>

        <h1 className="auth-title">
          {signingUp ? 'Start Free' : 'Sign in to AllRouted'}
        </h1>

        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}

        {signingUp ? (
          <>
            <label className="auth-field">
              <span>Your name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>
          </>
        ) : null}

        <label className="auth-field">
          <span>Email</span>
          <input
            ref={emailRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </label>

        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={signingUp ? 'new-password' : 'current-password'}
            /* The API refuses anything shorter, so say so before the round trip. */
            minLength={signingUp ? 10 : undefined}
            required
          />
          {signingUp ? (
            <em className="auth-help">At least 10 characters.</em>
          ) : (
            <button
              type="button"
              className="auth-forgot"
              onClick={() => {
                setMode('forgot');
                setError(null);
              }}
            >
              Forgot password?
            </button>
          )}
        </label>

        <button className="auth-submit" type="submit" disabled={busy}>
          {busy ? 'One moment…' : signingUp ? 'Create account' : 'Sign in'}
        </button>

        {providers?.google ? (
          <>
            <div className="auth-or">
              <span>or</span>
            </div>
            <button className="auth-google" type="button" onClick={google}>
              <GoogleMark />
              Continue with Google
            </button>
          </>
        ) : null}

        {/* Nothing at all when signup is closed, which is the ordinary state of
          * a working installation. A line explaining that there is no signup is
          * an answer to a question nobody asked -- everybody who reaches this
          * screen was given an account -- and it tells a stranger how the place
          * is organised. */}
        {providers?.signupOpen ? (
          <p className="auth-alt">
            {signingUp ? 'Already have an account?' : 'No account yet?'}{' '}
            <button
              type="button"
              onClick={() => {
                setMode(signingUp ? 'signin' : 'signup');
                setError(null);
              }}
            >
              {signingUp ? 'Sign in' : 'Create one'}
            </button>
          </p>
        ) : null}

        <p className="auth-back">
          <a href="https://allrouted.com">← Back to allrouted.com</a>
        </p>
      </form>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.93v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.93a9 9 0 0 0 0 8.1l3.04-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .93 4.95l3.04 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
